import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";
import axios from "axios";
import { toast } from "react-toastify";

const QuestionType = () => {
    const { examType, year } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [examTypeDetails, setExamTypeDetails] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("all");
    const [subjects, setSubjects] = useState([]);
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(year || "all");
    const [downloadStats, setDownloadStats] = useState({
        totalDownloads: 0,
        thisMonth: 0,
        thisWeek: 0,
        totalQuestions: 0,
        downloadGrowth: 0,
        popularSubjects: [],
        topPapers: [],
        hasActualData: false,
        statsSource: "loading"
    });
    
    // Payment States
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [userEmail, setUserEmail] = useState("info@eddcodesgh.com");
    const [userPhone, setUserPhone] = useState("");
    const [userName, setUserName] = useState("");
    const [paystackLoaded, setPaystackLoaded] = useState(false);
    
    // Shopping Cart
    const [cart, setCart] = useState([]);
    const [showCart, setShowCart] = useState(false);

    // User token (from localStorage or auth context)
    const [userToken, setUserToken] = useState("");

    // API Configuration
    const API_BASE_URL = "https://pastq.eddcodesgh.com/qApi/client";
    const API_KEY = "f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506";

    const apiClient = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${API_KEY}`,
            "Accept": "application/json"
        },
        withCredentials: true,
        timeout: 15000
    });

    // Exam type configuration
    const examTypeConfig = {
        "bece": {
            name: "BECE",
            fullName: "Basic Education Certificate Examination",
            description: "Junior High School final examination covering Mathematics, English, Science, Social Studies, and other core subjects.",
            icon: "bi-mortarboard",
            color: "primary",
            yearsRange: "2010-2024",
            totalPapers: 1500,
            popularSubjects: ["Mathematics", "English Language", "Integrated Science", "Social Studies"],
            price: 3.50, // GHS
            priceDescription: "per subject"
        },
        "wassce": {
            name: "WASSCE",
            fullName: "West African Senior School Certificate Examination",
            description: "Senior High School examination covering core and elective subjects for university admission.",
            icon: "bi-book",
            color: "success",
            yearsRange: "2008-2024",
            totalPapers: 3200,
            popularSubjects: ["Mathematics", "English", "Biology", "Chemistry", "Physics"],
            price: 4.50, // GHS
            priceDescription: "per subject"
        },
        "ctvet": {
            name: "CTVET",
            fullName: "National Business and Technical Examinations Board",
            description: "Technical and vocational examinations for business, trade, and industrial skills.",
            icon: "bi-gear",
            color: "warning",
            yearsRange: "2012-2024",
            totalPapers: 850,
            popularSubjects: ["Commerce", "Accounting", "Technical Drawing", "Auto Mechanics"],
            price: 4.50, // GHS
            priceDescription: "per subject"
        },
        "university": {
            name: "University",
            fullName: "University Past Questions",
            description: "Undergraduate level past questions from various universities and colleges.",
            icon: "bi-building",
            color: "info",
            yearsRange: "2015-2024",
            totalPapers: 2100,
            popularSubjects: ["Accounting", "Economics", "Computer Science", "Medicine"],
            price: 5.50, // GHS
            priceDescription: "per subject"
        }
    };

    // Load Paystack script
    useEffect(() => {
        if (!window.PaystackPop) {
            const script = document.createElement('script');
            script.src = 'https://js.paystack.co/v1/inline.js';
            script.async = true;
            script.onload = () => {
                setPaystackLoaded(true);
                console.log('Paystack script loaded successfully');
            };
            script.onerror = () => {
                console.error('Failed to load Paystack script');
                toast.error('Failed to load payment system. Please refresh the page.');
            };
            document.body.appendChild(script);

            return () => {
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
            };
        } else {
            setPaystackLoaded(true);
        }

        // Get user token from localStorage
        const token = localStorage.getItem('userToken') || localStorage.getItem('token') || '';
        setUserToken(token);
    }, []);

    useEffect(() => {
        checkPaymentStatus();
    }, []);

    useEffect(() => {
        if (examType) {
            fetchExamTypeData();
            fetchQuestions();
            fetchDownloadStats();
            loadCartFromStorage();
        }
    }, [examType, year]);

    const loadCartFromStorage = () => {
        const savedCart = localStorage.getItem(`cart_${examType}`);
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error("Error parsing cart from storage:", e);
                setCart([]);
            }
        }
    };

    const saveCartToStorage = (cartData) => {
        try {
            localStorage.setItem(`cart_${examType}`, JSON.stringify(cartData));
        } catch (e) {
            console.error("Error saving cart to storage:", e);
        }
    };

    const fetchExamTypeData = () => {
        const config = examTypeConfig[examType.toLowerCase()];
        if (config) {
            setExamTypeDetails(config);
            
            // Generate years for this exam type
            const currentYear = new Date().getFullYear();
            const startYear = parseInt(config.yearsRange.split('-')[0]);
            const yearsArray = Array.from(
                { length: currentYear - startYear + 1 },
                (_, i) => (currentYear - i).toString()
            );
            setYears(["all", ...yearsArray]);
        }
    };

    const fetchQuestions = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('exam_type', examType);
            if (year) params.append('year', year);

            const response = await apiClient.get(`/questions/getquestions.php?${params.toString()}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            if (response.data) {
                const questionsData = response.data.records || [];
                setQuestions(questionsData);
                setFilteredQuestions(questionsData);
                
                // Extract unique subjects
                const uniqueSubjects = [...new Set(questionsData.map(q => q.category_name))].filter(Boolean);
                setSubjects(["all", ...uniqueSubjects]);
            } else {
                toast.error("No questions found");
                setQuestions([]);
                setFilteredQuestions([]);
            }
        } catch (error) {
            toast.error("Failed to load questions");
            setQuestions([]);
            setFilteredQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchDownloadStats = async () => {
        try {
            const params = new URLSearchParams();
            params.append('exam_type', examType);
            if (selectedYear !== 'all') {
                params.append('year', selectedYear);
            }
            // Add cache busting parameter
            params.append('_t', Date.now());

            const response = await apiClient.get(`/downloads/download-stats.php?${params.toString()}`);

            if (response.data.status === "success") {
                const data = response.data.data;
                setDownloadStats({
                    totalDownloads: data.total_downloads || 0,
                    thisMonth: data.this_month_downloads || 0,
                    thisWeek: data.this_week_downloads || 0,
                    totalQuestions: data.total_questions || 0,
                    popularSubjects: data.popular_subjects || [],
                    topPapers: data.top_papers || [],
                    downloadGrowth: data.download_growth || 0,
                    hasActualData: data.has_actual_data || false,
                    statsSource: data.stats_source || 'estimated'
                });
            } else {
                // Fallback to default data
                setDownloadStats(getFallbackDownloadStats());
            }
        } catch (error) {
            console.error("Error fetching download stats:", error);
            // Fallback to default data
            setDownloadStats(getFallbackDownloadStats());
        }
    };

    const getFallbackDownloadStats = () => {
        const examConfig = examTypeConfig[examType.toLowerCase()];
        return {
            totalDownloads: Math.floor(Math.random() * 1000) + 500,
            thisMonth: Math.floor(Math.random() * 100) + 50,
            thisWeek: Math.floor(Math.random() * 30) + 10,
            totalQuestions: questions.length,
            popularSubjects: examConfig?.popularSubjects || [],
            topPapers: [],
            downloadGrowth: Math.floor(Math.random() * 30) + 5,
            hasActualData: false,
            statsSource: 'fallback'
        };
    };

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        filterQuestions(term, selectedSubject, selectedYear);
    };

    const handleSubjectChange = (subject) => {
        setSelectedSubject(subject);
        filterQuestions(searchTerm, subject, selectedYear);
    };

    const handleYearChange = (selectedYearValue) => {
        setSelectedYear(selectedYearValue);
        if (selectedYearValue === "all") {
            navigate(`/questions/${examType}`);
        } else {
            navigate(`/questions/${examType}/${selectedYearValue}`);
        }
        filterQuestions(searchTerm, selectedSubject, selectedYearValue);
        fetchDownloadStats(); // Refresh stats when year changes
    };

    const filterQuestions = (search, subject, year) => {
        let filtered = questions;
        
        if (search) {
            filtered = filtered.filter(q => 
                (q.title && q.title.toLowerCase().includes(search)) ||
                (q.description && q.description.toLowerCase().includes(search)) ||
                (q.category_name && q.category_name.toLowerCase().includes(search))
            );
        }
        
        if (subject !== "all") {
            filtered = filtered.filter(q => q.category_name === subject);
        }
        
        if (year !== "all") {
            filtered = filtered.filter(q => q.exam_year === year);
        }
        
        setFilteredQuestions(filtered);
    };

    // Add to cart function
    const addToCart = (question, type = "question") => {
        const existingItemIndex = cart.findIndex(item => 
            item.id === question.id && item.type === type
        );

        const item = {
            id: question.id,
            title: question.title,
            year: question.exam_year,
            subject: question.category_name,
            type: type,
            file: type === "question" ? question.question_file : question.answer_file,
            price: examTypeDetails?.price || 5,
            date: new Date().toISOString()
        };

        if (existingItemIndex === -1) {
            const newCart = [...cart, item];
            setCart(newCart);
            saveCartToStorage(newCart);
            toast.success(`${type === "question" ? "Question" : "Answer"} added to cart!`);
        } else {
            toast.info("Item already in cart");
        }
    };

    // Remove from cart
    const removeFromCart = (index) => {
        const newCart = cart.filter((_, i) => i !== index);
        setCart(newCart);
        saveCartToStorage(newCart);
        toast.success("Item removed from cart");
    };

    // Clear cart
    const clearCart = () => {
        setCart([]);
        localStorage.removeItem(`cart_${examType}`);
        toast.success("Cart cleared");
    };

    // Calculate total price
    const calculateTotal = () => {
        return cart.reduce((total, item) => total + item.price, 0);
    };

    // Paystack Payment Integration
    const initiatePaystackPayment = async () => {
    if (!userName || !userEmail) {
        toast.error("Please fill in your name and email");
        return;
    }

    if (!paystackLoaded) {
        toast.warn('Payment system is loading, please try again in a moment');
        return;
    }

    setPaymentProcessing(true);

    try {
        const totalAmount = calculateTotal();
        const amountInPesewas = totalAmount * 100;
        const transactionRef = `PASTQ-${examType.toUpperCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Use your Paystack public key directly
        const paystackKey = 'pk_live_5f6546ce31829ad520e7dcbe49c40c751f01fdca'; // Test key
        // For production: 'pk_live_5f6546ce31829ad520e7dcbe49c40c751f01fdca' pk_test_5ea8be3af44d69cb7e9bcef8e8dfef9b23b4e5f2

        const orderData = {
            orderId: transactionRef,
            cart: cart,
            examType,
            totalAmount,
            userEmail,
            userName,
            userToken
        };
        
        localStorage.setItem('current_order', JSON.stringify(orderData));

        const handler = window.PaystackPop.setup({
            key: paystackKey,
            email: userEmail,
            amount: amountInPesewas,
            currency: 'GHS',
            ref: transactionRef,
            firstname: userName.split(' ')[0],
            lastname: userName.split(' ').slice(1).join(' ') || '',
            metadata: {
                custom_fields: [
                    {
                        display_name: "User Token",
                        variable_name: "user_token",
                        value: userToken || "guest"
                    },
                    {
                        display_name: "User Name",
                        variable_name: "user_name",
                        value: userName
                    },
                    {
                        display_name: "User Email",
                        variable_name: "user_email",
                        value: userEmail
                    },
                    {
                        display_name: "User Phone",
                        variable_name: "user_phone",
                        value: userPhone || ""
                    },
                    {
                        display_name: "Exam Type",
                        variable_name: "exam_type",
                        value: examType
                    },
                    {
                        display_name: "Cart Items Count",
                        variable_name: "items_count",
                        value: cart.length.toString()
                    },
                    {
                        display_name: "Cart Total",
                        variable_name: "cart_total",
                        value: calculateTotal().toString()
                    }
                ]
            },
            callback: (response) => {
                verifyPayment(response.reference, transactionRef);
            },
            onClose: () => {
                setPaymentProcessing(false);
                toast.info('Payment window closed');
            }
        });

        handler.openIframe();

    } catch (error) {
        toast.error(error.message || "Payment initiation failed. Please try again.");
        setPaymentProcessing(false);
    }
};

    // Verify payment with your backend
    const verifyPayment = async (reference, orderId) => {
        try {
            const response = await apiClient.post('/payments/verify_payment.php', {
                reference: reference,
                userToken: userToken,
                userName: userName,
                userEmail: userEmail,
                userPhone: userPhone,
                cart: cart,
                examType: examType
            });

            if (response.data.status === 201) {
                clearCart();
                setShowPaymentModal(false);
                
                toast.success(
                    <div>
                        <strong>Payment Successful!</strong>
                        <br />
                        <small>You can now download your purchased items.</small>
                    </div>
                );

                if (response.data.purchasedItems) {
                    localStorage.setItem('purchased_items', JSON.stringify(response.data.purchasedItems));
                    localStorage.setItem('last_payment_reference', reference);
                }

                navigate(`/success/${examType}/thank-you`, {
                    state: {
                        transactionId: response.data.transaction_id || reference,
                        orderId: orderId,
                        amount: response.data.amount || calculateTotal(),
                        items: response.data.purchasedItems || [],
                        downloadUrls: response.data.download_urls || []
                    }
                });
                
            } else {
                throw new Error(response.data.message || 'Payment verification failed');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Payment verification failed");
            navigate(`/questions/${examType}/payment-failed`);
        }
    };

    // Check payment status from callback
    const checkPaymentStatus = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const reference = urlParams.get('reference');
        const status = urlParams.get('status');

        if (reference) {
            try {
                await verifyPayment(reference, reference);
            } catch (error) {
                console.error("Payment status check error:", error);
            }
        }
    };

    const goBack = () => {
        navigate("/questions");
    };

    if (!examTypeDetails) {
        return (
            <>
                <Navbar />
                <main className="main-content">
                    <div className="container py-5">
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading exam type details...</p>
                        </div>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            
            <main className="main-content">
                {/* Cart Icon */}
                <div className="cart-icon-container position-fixed mt-4" style={{ top: '100px', right: '20px', zIndex: 1000 }}>
                    <button 
                        className="btn btn-primary position-relative"
                        onClick={() => setShowCart(!showCart)}
                    >
                        <i className="bi bi-cart3"></i>
                        {cart.length > 0 && (
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                {cart.length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Cart Modal */}
                {showCart && (
                    <div className="cart-modal position-fixed" style={{
                        top: '190px',
                        right: '20px',
                        width: '350px',
                        zIndex: 1000,
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        boxShadow: '0 5px 20px rgba(0,0,0,0.15)',
                        padding: '20px',
                        maxHeight: '450px',
                        overflowY: 'auto'
                    }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Shopping Cart</h6>
                            <button 
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowCart(false)}
                            >
                                <i className="bi bi-x"></i>
                            </button>
                        </div>
                        
                        {cart.length === 0 ? (
                            <p className="text-muted text-center py-3">Your cart is empty</p>
                        ) : (
                            <>
                                {cart.map((item, index) => (
                                    <div key={index} className="cart-item border-bottom pb-2 mb-2">
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <small className="fw-bold d-block">{item.subject} - {item.year}</small>
                                                <small className="text-muted">{item.type === "question" ? "Question Paper" : "Answer Sheet"}</small>
                                            </div>
                                            <div className="text-end">
                                                <div className="fw-bold">GHS {item.price}</div>
                                                <button 
                                                    className="btn btn-sm btn-link text-danger p-0"
                                                    onClick={() => removeFromCart(index)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                <div className="mt-3">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Total:</span>
                                        <span className="fw-bold">GHS {calculateTotal()}</span>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button 
                                            className="btn btn-sm btn-outline-danger flex-grow-1"
                                            onClick={clearCart}
                                        >
                                            Clear Cart
                                        </button>
                                        <button 
                                            className="btn btn-sm btn-primary flex-grow-1"
                                            onClick={() => {
                                                setShowCart(false);
                                                setShowPaymentModal(true);
                                            }}
                                        >
                                            Checkout
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Paystack Payment Modal */}
                {showPaymentModal && (
                    <div className="modal-backdrop mt-5" style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1050,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div className="modal-content" style={{
                            backgroundColor: 'white',
                            borderRadius: '10px',
                            padding: '25px',
                            width: '90%',
                            maxWidth: '500px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="modal-title">Complete Payment with Paystack</h5>
                                <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setShowPaymentModal(false)}
                                    disabled={paymentProcessing}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                            
                            <div className="mb-4">
                                <div className="alert alert-info">
                                    <i className="bi bi-credit-card me-2"></i>
                                    You are purchasing {cart.length} item(s) for <strong>GHS {calculateTotal()}</strong>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label">Full Name *</label>
                                <input 
                                    type="text" 
                                    className="form-control"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                    placeholder="Enter your full name"
                                    required
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label">Email Address *</label>
                                <input 
                                    type="email" 
                                    className="form-control"
                                    value={userEmail}
                                    onChange={(e) => setUserEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                />
                                <small className="text-muted">Paystack will send payment receipt to this email</small>
                            </div>
                            
                            <div className="mb-4">
                                <label className="form-label">Phone Number (Optional)</label>
                                <input 
                                    type="tel" 
                                    className="form-control"
                                    value={userPhone}
                                    onChange={(e) => setUserPhone(e.target.value)}
                                    placeholder="Enter your phone number"
                                />
                            </div>
                            
                            <div className="mb-4">
                                <div className="alert alert-warning">
                                    <i className="bi bi-shield-check me-2"></i>
                                    <strong>Secure Payment:</strong> All payments are processed securely via Paystack. You can pay with Mobile Money, Visa, MasterCard, or other supported methods.
                                </div>
                            </div>
                            
                            <div className="d-flex gap-2">
                                <button 
                                    className="btn btn-outline-secondary flex-grow-1"
                                    onClick={() => setShowPaymentModal(false)}
                                    disabled={paymentProcessing}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary flex-grow-1"
                                    onClick={initiatePaystackPayment}
                                    disabled={paymentProcessing || !userName || !userEmail || !paystackLoaded}
                                >
                                    {paymentProcessing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Processing...
                                        </>
                                    ) : !paystackLoaded ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-credit-card me-2"></i>
                                            Pay GHS {calculateTotal()}
                                        </>
                                    )}
                                </button>
                            </div>
                            
                            {!paystackLoaded && (
                                <div className="mt-3 text-center">
                                    <small className="text-muted">
                                        <i className="bi bi-hourglass-split me-1"></i>
                                        Payment system is loading...
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Hero Section */}
                <section className={`py-5 bg-${examTypeDetails.color} bg-opacity-10`}>
                    <div className="container mt-5">
                        <div className="row align-items-center">
                            <div className="col-lg-7">
                                <nav aria-label="breadcrumb" className="mb-3">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item">
                                            <Link to="/questions" className="text-decoration-none">
                                                <i className="bi bi-house-door me-1"></i>
                                                All Exams
                                            </Link>
                                        </li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                            <i className={`bi ${examTypeDetails.icon} me-1`}></i>
                                            {examTypeDetails.name}
                                        </li>
                                    </ol>
                                </nav>
                                
                                <div className="d-flex align-items-start mb-3">
                                    <div className={`bg-${examTypeDetails.color} rounded-3 p-3 me-3`}>
                                        <i className={`bi ${examTypeDetails.icon} text-white fs-3`}></i>
                                    </div>
                                    <div>
                                        <h1 className="display-5 fw-bold mb-2">{examTypeDetails.name}</h1>
                                        <p className="lead mb-0">{examTypeDetails.fullName}</p>
                                    </div>
                                </div>
                                
                                <p className="text-muted mb-4">{examTypeDetails.description}</p>
                                
                                <div className="d-flex flex-wrap gap-3">
                                    <span className="badge bg-white text-dark border px-3 py-2">
                                        <i className="bi bi-calendar-range me-1"></i>
                                        {examTypeDetails.yearsRange}
                                    </span>
                                    <span className="badge bg-white text-dark border px-3 py-2">
                                        <i className="bi bi-files me-1"></i>
                                        {questions.length} Papers Available
                                    </span>
                                    <span className="badge bg-primary text-white px-3 py-2">
                                        <i className="bi bi-tag me-1"></i>
                                        GHS {examTypeDetails.price} {examTypeDetails.priceDescription}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="col-lg-5">
                                {/* <div className="card border-0 shadow-sm">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="card-title mb-0">
                                                <i className="bi bi-graph-up me-2"></i>
                                                Download Statistics
                                            </h6>
                                            {downloadStats.hasActualData ? (
                                                <span className="badge bg-success">
                                                    <i className="bi bi-check-circle me-1"></i>
                                                    Live Data
                                                </span>
                                            ) : (
                                                <span className="badge bg-warning" title="Based on estimates">
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    Estimated
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="row text-center">
                                            <div className="col-4">
                                                <div className="p-2">
                                                    <div className="h4 mb-0 text-primary">{downloadStats.totalDownloads.toLocaleString()}</div>
                                                    <small className="text-muted">Total Downloads</small>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className="p-2">
                                                    <div className="h4 mb-0 text-success">{downloadStats.thisMonth.toLocaleString()}</div>
                                                    <small className="text-muted">This Month</small>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className="p-2">
                                                    <div className="h4 mb-0 text-warning">{downloadStats.thisWeek.toLocaleString()}</div>
                                                    <small className="text-muted">This Week</small>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-3 text-center">
                                            <small className={`${downloadStats.downloadGrowth > 0 ? 'text-success' : 'text-danger'}`}>
                                                <i className={`bi ${downloadStats.downloadGrowth > 0 ? 'bi-arrow-up' : 'bi-arrow-down'} me-1`}></i>
                                                {Math.abs(downloadStats.downloadGrowth)}% {downloadStats.downloadGrowth > 0 ? 'increase' : 'decrease'} from last month
                                            </small>
                                        </div>
                                        
                                        <div className="mt-3">
                                            <h6 className="small text-muted mb-2">Popular Subjects:</h6>
                                            <div className="d-flex flex-wrap gap-1">
                                                {downloadStats.popularSubjects.slice(0, 4).map((subject, index) => (
                                                    <span key={index} className="badge bg-light text-dark border">
                                                        {subject}
                                                    </span>
                                                ))}
                                                {downloadStats.popularSubjects.length > 4 && (
                                                    <span className="badge bg-secondary">
                                                        +{downloadStats.popularSubjects.length - 4} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="mt-3 pt-2 border-top">
                                            <small className="text-muted d-flex justify-content-between">
                                                <span>Available Questions:</span>
                                                <span className="fw-semibold">{downloadStats.totalQuestions || questions.length}</span>
                                            </small>
                                        </div>
                                        
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-4 bg-light">
                    <div className="container">
                        <div className="row g-3">
                            <div className="col-lg-4">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search papers by title or subject..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                </div>
                            </div>
                            
                            <div className="col-lg-4">
                                <select 
                                    className="form-select"
                                    value={selectedSubject}
                                    onChange={(e) => handleSubjectChange(e.target.value)}
                                >
                                    <option value="all">All Subjects</option>
                                    {subjects.filter(s => s !== "all").map(subject => (
                                        <option key={subject} value={subject}>{subject}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="col-lg-4">
                                <select 
                                    className="form-select"
                                    value={selectedYear}
                                    onChange={(e) => handleYearChange(e.target.value)}
                                >
                                    <option value="all">All Years</option>
                                    {years.filter(y => y !== "all").map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted">
                                        Showing {filteredQuestions.length} of {questions.length} papers
                                        {selectedSubject !== "all" && ` in ${selectedSubject}`}
                                        {selectedYear !== "all" && ` for ${selectedYear}`}
                                    </small>
                                </div>
                                <div>
                                    <button 
                                        className="btn btn-sm btn-outline-secondary me-2"
                                        onClick={goBack}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i>
                                        Back
                                    </button>
                                    {cart.length > 0 && (
                                        <button 
                                            className="btn btn-sm btn-success me-2"
                                            onClick={() => setShowPaymentModal(true)}
                                        >
                                            <i className="bi bi-credit-card me-1"></i>
                                            Pay Now (GHS {calculateTotal()})
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Papers Section */}
                <section className="py-5">
                    <div className="container">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading question papers...</p>
                            </div>
                        ) : filteredQuestions.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="bi bi-file-earmark-x display-1 text-muted"></i>
                                <h4 className="mt-3">No Papers Found</h4>
                                <p className="text-muted mb-4">
                                    {searchTerm || selectedSubject !== "all" || selectedYear !== "all" 
                                        ? "Try adjusting your filters"
                                        : "No papers available for this exam type"}
                                </p>
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={() => {
                                        setSearchTerm("");
                                        setSelectedSubject("all");
                                        setSelectedYear("all");
                                    }}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Reset Filters
                                </button>
                            </div>
                        ) : (
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="row">
                                        {filteredQuestions.map(question => (
                                            <div key={question.id} className="col-md-3 mb-4">
                                                <div className="card border-0 shadow-sm h-100">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div>
                                                                <h6 className="card-title mb-1 text-truncate" title={question.title}>
                                                                    {question.title} - {question.exam_year}
                                                                </h6>
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    <span className="badge bg-light text-dark border">
                                                                        <i className="bi bi-calendar me-1"></i>
                                                                        {question.exam_year}
                                                                    </span>
                                                                    {question.category_name && (
                                                                        <span className="badge bg-light text-dark border">
                                                                            {question.category_name}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className="badge bg-success bg-opacity-10 text-success">
                                                                <i className="bi bi-file-pdf"></i> PDF
                                                            </span>
                                                        </div>
                                                        
                                                        <div className="mb-3">
                                                            <small className="text-muted d-block mb-1">
                                                                <i className="bi bi-clock me-1"></i>
                                                                Uploaded: {question.created_date}
                                                            </small>
                                                            <small className="text-muted">
                                                                <i className="bi bi-file-earmark me-1"></i>
                                                                Size: ~{(Math.random() * 5 + 1).toFixed(1)} MB
                                                            </small>
                                                        </div>
                                                        
                                                        <div className="row g-2">
                                                            <div className="col-6">
                                                                <button
                                                                    className="btn btn-outline-danger text-dark w-100"
                                                                    onClick={() => addToCart(question, "question")}
                                                                    title="Add to Cart - GHS 3.50p"
                                                                >
                                                                    <i className="bi bi-cart-plus me-1"></i>
                                                                    Add Questions
                                                                </button>
                                                            </div>
                                                            <div className="col-6">
                                                                <button
                                                                    className={`btn w-100 ${question.answer_file === null ? "btn-secondary" : "btn-outline-success"}`}
                                                                    onClick={() => addToCart(question, "answer")}
                                                                    title={question.answer_file === null ? "Answers not available" : "Add to Cart - GHS 3.50p"}
                                                                    disabled={question.answer_file === null}
                                                                    style={{ cursor: question.answer_file === null ? "not-allowed" : "pointer" }}
                                                                >
                                                                    <i className="bi bi-cart-plus me-1"></i>
                                                                    {question.answer_file === null ? "Answers" : "Add Answers"}
                                                                </button>
                                                            </div>

                                                        </div>
                                                    </div>
                                                    <div className="card-footer bg-transparent border-top">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                           
                                                            <small className="text-primary">
                                                                GHS {examTypeDetails.price}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
                
                <section className="py-5 bg-light">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-8 mx-auto text-center">
                                <h5 className="mb-3">
                                    <i className="bi bi-credit-card me-2"></i>
                                    Secure Payment with Paystack
                                </h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <div className="card border-0 bg-white h-100">
                                            <div className="card-body">
                                                <i className="bi bi-shield-check display-6 text-success mb-3"></i>
                                                <h6>Secure Payment</h6>
                                                <p className="small text-muted mb-0">
                                                    All payments are processed securely via Paystack
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <div className="card border-0 bg-white h-100">
                                            <div className="card-body">
                                                <i className="bi bi-phone display-6 text-primary mb-3"></i>
                                                <h6>Mobile Money</h6>
                                                <p className="small text-muted mb-0">
                                                    Pay with MTN, Vodafone, AirtelTigo
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <div className="card border-0 bg-white h-100">
                                            <div className="card-body">
                                                <i className="bi bi-credit-card-2-front display-6 text-warning mb-3"></i>
                                                <h6>Card Payments</h6>
                                                <p className="small text-muted mb-0">
                                                    Visa, MasterCard & other cards accepted
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <style jsx>{`
            /* Add to your component's style section */
            .stats-card {
                border-radius: 12px;
                transition: all 0.3s ease;
            }

            .stats-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
            }

            .badge {
                padding: 0.25em 0.6em;
                font-size: 0.75em;
                font-weight: 500;
            }

            .list-group-item {
                background: transparent;
                border-color: rgba(0, 0, 0, 0.05);
            }
                .hero-section {
                    background: linear-gradient(135deg, rgba(13, 110, 253, 0.05) 0%, rgba(111, 66, 193, 0.05) 100%);
                }
                
                .card {
                    border-radius: 12px;
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
                }
                
                .cart-icon-container {
                    animation: bounce 2s infinite;
                }
                
                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                
                .cart-modal {
                    animation: slideIn 0.3s ease;
                }
                
                @keyframes slideIn {
                    from {
                        transform: translateX(100px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                .modal-backdrop {
                    animation: fadeIn 0.3s ease;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                .btn-outline-danger:hover {
                    background-color: rgba(220, 53, 69, 0.1);
                }
                
                .btn-outline-success:hover {
                    background-color: rgba(25, 135, 84, 0.1);
                }
                
                @media (max-width: 768px) {
                    .display-5 {
                        font-size: 2rem;
                    }
                    
                    .col-md-6 {
                        margin-bottom: 1rem;
                    }
                    
                    .cart-icon-container {
                        top: 80px;
                        right: 15px;
                    }
                    
                    .cart-modal {
                        width: 90% !important;
                        right: 5% !important;
                    }
                }
            `}</style>
        </>
    );
};

export default QuestionType;